import React, { useEffect, useRef, useState } from 'react';
import { signIn as amplifySignIn, resetPassword, fetchAuthSession, AuthError } from 'aws-amplify/auth'
import { Image, StyleProp, StyleSheet, Text, TextStyle, View } from 'react-native';
import { DNABox, DNAButton, DNAText, Iffy, InformationMessage, luxColors } from '@alucio/lux-ui';
import InputComponent from 'src/components/Publishers/InputComponent';
import FormWrapper from '../FormWrapper';
import {
  cleanDisconnectedDue,
  getDisconnectedDue,
  setDisconnectedDue,
  setDisconnectedDueDefault,
} from 'src/components/Authenticator/DisconnectedDue';
import useSSO from 'src/components/DNA/hooks/useSso';
import { Input } from '@ui-kitten/components';
import { useHistory, useLocation } from 'src/router';
import { SessionStatus } from 'src/components/IdleComponent/IdleComponent';
import { useAppSettings } from 'src/state/context/AppSettings';
import { useOnLeave } from 'src/hooks/useConfirmLeave/useConfirmLeave';
import { isIOS } from 'react-device-detect';
import * as logger from 'src/utils/logger';
import useSignedIn from 'src/components/Authenticator/services/SignedStateProvider';
import RequireNewPassword from '../Password/RequireNewPassword/RequireNewPassword';
import { getAuthenticationCopy, getAuthenticationLayout } from '../authenticationLayout';

const BeaconLoginLogo = require('../../../../assets/images/LoginLogo.svg');

const styles = StyleSheet.create({
  beaconText: {
    color: luxColors.alucioPurple.primary,
    fontSize: 24,
    fontWeight: 'bold',
    paddingBottom: 20,
    paddingTop: 20,
  },
  content: {
    flexDirection: 'column',
    width: '100%',
  },
  errorWrapper: {
    height: 20,
    marginTop: 20,
    marginBottom: 10,
  },
  invalidMessage: {
    color: luxColors.error.primary,
    fontSize: 12,
  },
  timeExpired: {
    color: luxColors.warning.quaternary,
    fontSize: 12,
  },
  logOut: {
    color: luxColors.success.tertiary,
    fontSize: 12,
  },
  marginTop: {
    marginTop: 20,
  },
  v2LoginError: {
    marginTop: 8,
  },
  loginLogoImage: {
    height: 80,
    resizeMode: 'contain',
    width: 246,
  },
});

interface CustomSignInProps {
  allowChangePassword?: (isSSO: boolean) => void;
  disconnectedDueMsg: DisconnectedDueMessage;
  hideInactivityMessage?: boolean;
  onDisconnectedDueChange?: () => void;
  setIsForcePasswordChange?: (value: boolean) => void;
}

interface DisconnectedDueMessage {
  msg?: string;
  type?: string;
  visible?: boolean;
}

export interface SignInFailureResponse {
  code: string,
  message: string,
  name: string,
}

const PASSWORD_RESET_REQUIRED = 'PasswordResetRequiredException';
const USER_NOT_FOUND = 'UserNotFoundException';
const NOT_AUTHORIZED = 'NotAuthorizedException';
const INVALID_INPUTS = 'InvalidParameterException';
const INVALID_LOGIN_ERRORS = [USER_NOT_FOUND, NOT_AUTHORIZED, INVALID_INPUTS];

const CustomSignIn = (props: CustomSignInProps) => {
  const { isOnline, isPWAStandalone } = useAppSettings()
  const [password, setPassword] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [disabled, setDisabled] = useState<boolean>(!isOnline);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [invalidLogin, setInvalidLogin] = useState<boolean>(false);
  const { CheckSSOAndSignInWithRedirect, isSSoUser, ssoError, cleanSSoError } = useSSO();
  const { setSignInUsername } = useSignedIn();
  const passwordInputRef = useRef<Input>();
  const emailInputRef = useRef<Input>(null)
  useOnLeave(cleanDisconnectedDue)
  const disabledLogInButton = !isOnline || (showPassword ? disabled || !password : disabled || !email);
  const { disconnectedDueMsg } = props;
  const location = useLocation()
  const history = useHistory()
  const authenticationCopy = getAuthenticationCopy(location.search);
  const isAuthenticationV2 = getAuthenticationLayout(location.search) === 'new';
  const hasAuthenticationMessage = Boolean(
    ssoError || invalidLogin || disconnectedDueMsg.visible,
  );
  const showV2InvalidLogin = isAuthenticationV2 && invalidLogin && !ssoError;
  const authenticationWarning = ssoError || (
    invalidLogin ? authenticationCopy.invalidLoginWarning : undefined
  );
  useEffect(() => {
    logger.auth.signIn.info('Component SigIn mounted')

    // If a logout reason was provided in the URL we want to save it to disconnected due
    const queryParams = new URLSearchParams(location.search)
    if (queryParams.has('logoutFor')) {
      const reason = queryParams.get('logoutFor') as SessionStatus
      // Validate Input from Query String
      if ([SessionStatus.end, SessionStatus.timeExpired].includes(reason)) {
        setDisconnectedDue(reason)
        props.onDisconnectedDueChange?.();
        logger.auth.signOut.info(`Logout reason detected: ${reason}`);
      }
      queryParams.delete('logoutFor')
      history.replace({
        search: queryParams.toString(),
      })
    }

    const onPasteEvent = (e) => {
      const pastedText = e.clipboardData.getData('text')
      if (pastedText) {
        onEmailChange(pastedText)
      }
    }

    let emailInput: HTMLInputElement | null = null
    if (isIOS) {
      // eslint-disable-next-line dot-notation
      emailInput = emailInputRef.current?.['textInputRef']['current']
      if (emailInput) {
        emailInput.addEventListener('paste', onPasteEvent)
      }
    }

    return () => {
      if (isIOS && emailInput) {
        emailInput.removeEventListener('paste', onPasteEvent)
      }
      logger.auth.signIn.info('Component Sigin unmounted')
    };
  }, []);

  useEffect(() => {
    if (!showPassword && isOnline) {
      setDisabled(false);
      logger.auth.signIn.info('Show password set to false and online, enabling inputs')
    }
  }, [showPassword, isOnline]);

  useEffect(() => {
    if (showPassword) {
      passwordInputRef.current?.focus()
    }
  }, [showPassword]);

  useEffect(() => {
    props.allowChangePassword?.(!isSSoUser && isOnline)
  }, [isSSoUser, isOnline])

  function onPasswordChange(pwd: string): void {
    if (!pwd.endsWith(' ')) {
      setPassword(pwd);
    }
  }

  async function onEmailChange(val: string): Promise<void> {
    setEmail(val.toLowerCase());
    isPWAStandalone && setDisabled(false);
  }

  async function signIn(): Promise<void> {
    setDisabled(true);
    setInvalidLogin(false);
    setDisconnectedDueDefault();
    props.onDisconnectedDueChange?.();
    logger.auth.signIn.info('Signing in');
    const username = email.toLocaleLowerCase().trim()
    try {
      const signinResponse = await amplifySignIn({
        username,
        password,
      });
      logger.auth.signIn.info(`Got response ${signinResponse.nextStep.signInStep}`);
      if (signinResponse.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setSignInUsername(username)
        props?.setIsForcePasswordChange?.(true);
      } else if (signinResponse.isSignedIn) {
        const authSession = await fetchAuthSession()
        const tenantId = authSession.tokens?.idToken?.payload['custom:org_id'] || ''
        const userId = authSession.tokens?.idToken?.payload['custom:user_id'] || ''
        const email = authSession.tokens?.idToken?.payload.email + ''
        const role = authSession.tokens?.accessToken.payload['cognito:groups']

        // Identify is used so Segment knows who is logged, so it links tracked actions to that user
        analytics?.identify(email.trim(), {
          userId,
          tenantId,
          email,
          role,
        }, function () {
          analytics?.track('LOGIN_LOGIN', {
            action: 'LOGIN',
            category: 'LOGIN',
          });
        });

        // reload the page to have the app load signed in
        window.location.reload();
      } else {
        logger.auth.signIn.error(`Sign in response: Unhandled next step: ${signinResponse.nextStep.signInStep}`);
      }
    } catch (e) {
      logger.auth.signIn.error('Sign in failed', e);
      /** TODO: this should be refactored to create a more definitive response
       * interface with constrained props rather than general string typings */
      if (e instanceof AuthError) {
        if (INVALID_LOGIN_ERRORS.includes(e.name)) {
          setInvalidLogin(true);
          setDisabled(false);
          logger.auth.signIn.warn('Invalid login attempt');
        } else if (e.name === PASSWORD_RESET_REQUIRED) {
          logger.auth.signIn.info('Password reset required');
          await resetPassword({ username })
        }
      }
    }
  }

  async function doContinue(): Promise<void> {
    cleanDisconnectedDue()
    props.onDisconnectedDueChange?.();
    setDisabled(true);
    setInvalidLogin(false);
    cleanSSoError();
    logger.auth.signIn.info('Continuing SSO validation');

    try {
      const isSSO = await CheckSSOAndSignInWithRedirect(email);
      if (isSSO) {
        logger.auth.signIn.debug('Is SSO User Should be redirecting.....');
      } else {
        setShowPassword(true);
        setDisabled(false);
      }
    } catch (e) {
      logger.auth.signIn.error('SSO validation failed', e);
      setDisabled(false);
    }
  }

  const inputStyle: StyleProp<TextStyle> = showPassword
    /** This style is needed to make the username visible on ipad */
    ? { opacity: 1, color: 'black' }
    : null

  return (
    <>
      <Iffy is={!isAuthenticationV2}>
        <Image
          testID="legacy-login-logo"
          source={BeaconLoginLogo}
          style={styles.loginLogoImage}
        />
      </Iffy>
      <View style={styles.content}>
        <Iffy
          is={!isAuthenticationV2 || (
            hasAuthenticationMessage && !showV2InvalidLogin && !props.hideInactivityMessage
          )}
        >
          <View style={[styles.errorWrapper, ssoError ? { marginBottom: 30 } : null]}>
            <Iffy is={ssoError}>
              <Text style={styles.invalidMessage}>{authenticationWarning}</Text>
            </Iffy>
            <Iffy is={invalidLogin}>
              <Text
                testID="login-error"
                style={styles.invalidMessage}
              >
                {authenticationWarning}
              </Text>
            </Iffy>
            <Iffy is={disconnectedDueMsg.visible && disconnectedDueMsg.type === 'logout'}>
              <Text style={styles.logOut}>{disconnectedDueMsg.msg}</Text>
            </Iffy>
            <Iffy is={disconnectedDueMsg.visible && disconnectedDueMsg.type === 'timeExpired'}>
              <Text style={styles.timeExpired}>{disconnectedDueMsg.msg}</Text>
            </Iffy>
          </View>
        </Iffy>
        <Iffy is={!isOnline}>
          <InformationMessage
            variance="warning"
            text="Login is unavailable offline."
          />
        </Iffy>

        <InputComponent
          inputStyle={inputStyle}
          disabled={showPassword || !isOnline}
          isPassword={false}
          placeHolder={authenticationCopy.emailPlaceholder}
          onChangeText={onEmailChange}
          onSubmitEditing={disabledLogInButton ? () => null : doContinue}
          removeMarginPadding={true}
          required={false}
          testID="tb-username"
          title={authenticationCopy.emailLabel}
          ref={emailInputRef}
          defaultValue={email}
          autoFocus={true}
          autoCapitalize="none"
          autoCorrect={false}
          autoCompleteType="off"
        />

        <DNABox style={{ display: showPassword ? undefined : 'none' }} appearance="col">
          <InputComponent
            disabled={disabled}
            isPassword={true}
            placeHolder="Password"
            onChangeText={onPasswordChange}
            onSubmitEditing={disabledLogInButton ? () => null : signIn}
            ref={passwordInputRef as any}
            removeMarginPadding={true}
            required={false}
            testID="tb-password"
            title="PASSWORD"
            value={password}
            status={showV2InvalidLogin ? 'danger' : undefined}
          />
          <Iffy is={showV2InvalidLogin}>
            <DNAText testID="v2-login-error" c1 status="danger" style={styles.v2LoginError}>
              {authenticationWarning}
            </DNAText>
          </Iffy>
        </DNABox>

        <DNABox
          testID="authentication-submit-actions"
          spacing={isAuthenticationV2 ? 'md' : 'xl'}
          childFill={isAuthenticationV2 ? 1 : undefined}
          childStyle={{
            flex: isAuthenticationV2 ? undefined : 1,
            marginBottom: isAuthenticationV2 ? 0 : 16,
            marginTop: isAuthenticationV2 ? 20 : 12,
          }}
        >
          <Iffy is={showPassword}>
            <DNABox
              testID="authentication-back-action"
              fill={!isAuthenticationV2}
              alignY="center"
            >
              <DNAButton
                appearance={isAuthenticationV2 ? 'outline' : 'ghost'}
                status={isAuthenticationV2 ? 'secondary' : 'primary'}
                size={isAuthenticationV2 ? 'xl' : 'lg'}
                onPress={() => {
                  setDisabled(false);
                  setInvalidLogin(false);
                  setPassword('')
                  setShowPassword(false)
                  logger.auth.signIn.info('Back to email input')
                }}
                children="Back"
              />
            </DNABox>
          </Iffy>
          <DNABox testID="authentication-login-action" fill alignX="end">

            <DNAButton
              testID="submit-login"
              status={'primary'}
              size={isAuthenticationV2 ? 'xl' : undefined}
              onPress={showPassword ? signIn : doContinue}
              disabled={disabledLogInButton}
              stretch={isAuthenticationV2 ? true : undefined}
              children={showPassword ? 'Log in' : 'Continue'}
            />
          </DNABox>
        </DNABox>
      </View>
    </>
  );
};

CustomSignIn.displayName = 'CustomSignIn';

const SignInWrapper = (props) => {
  const [changePasswordEnabled, setChangePasswordEnabled] = useState<boolean>(false);
  const [isForcePasswordChange, setIsForcePasswordChange] = useState<boolean>(false);
  const location = useLocation();
  const authenticationCopy = getAuthenticationCopy(location.search);
  const [disconnectedDueMsg, setDisconnectedDueMsg] = useState<DisconnectedDueMessage>(
    () => getDisconnectedDue(),
  );
  const showInactivitySignedOut = (
    getAuthenticationLayout(location.search) === 'new' &&
    disconnectedDueMsg.visible &&
    disconnectedDueMsg.type === 'timeExpired'
  );

  function refreshDisconnectedDueMsg(): void {
    setDisconnectedDueMsg(getDisconnectedDue());
  }

  return isForcePasswordChange
    ? <RequireNewPassword /> : (
      <FormWrapper
        isLogin={true}
        {...props}
        changePasswordEnabled={changePasswordEnabled}
        title={showInactivitySignedOut ? "You've been signed out" : authenticationCopy.signInTitle}
      >
        <CustomSignIn
          {...props}
          disconnectedDueMsg={disconnectedDueMsg}
          hideInactivityMessage={showInactivitySignedOut}
          onDisconnectedDueChange={refreshDisconnectedDueMsg}
          setIsForcePasswordChange={(val) => setIsForcePasswordChange(val)}
          allowChangePassword={(isAllowed) => setChangePasswordEnabled(isAllowed)}
        />
      </FormWrapper>
    );
};

export default SignInWrapper;
